import { useState, useEffect, useCallback, useRef } from 'react';
import { api, fmt } from '../api';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import NavIcon from '../components/NavIcon';
import DateField from '../components/DateField';

function PurchaseModal({ products, suppliers, onClose, onSave }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [supplierId, setSupplierId] = useState('');
  const [note, setNote] = useState('');
  const [items, setItems] = useState([{ productId: '', quantity: 1, unitCost: '' }]);
  const [saving, setSaving] = useState(false);

  const addItem = () => setItems(i => [...i, { productId: '', quantity: 1, unitCost: '' }]);
  const removeItem = (idx) => setItems(i => i.filter((_, j) => j !== idx));
  const setItem = (idx, k, v) => setItems(i => {
    const next = [...i]; next[idx] = { ...next[idx], [k]: v };
    if (k === 'productId' && v) { const p = products.find(p => p.id === Number(v)); if (p) next[idx].unitCost = p.costPrice; }
    return next;
  });
  const total = items.reduce((s, i) => s + (Number(i.quantity) * Number(i.unitCost) || 0), 0);

  const save = async () => {
    const valid = items.filter(i => i.productId && i.quantity > 0 && i.unitCost >= 0);
    if (!valid.length) return toast.error('Add at least one valid item');
    setSaving(true);
    try {
      await api.createPurchase({ date: new Date(date).toISOString(), supplierId: supplierId ? Number(supplierId) : null, note, items: valid.map(i => ({ productId: Number(i.productId), quantity: Number(i.quantity), unitCost: Number(i.unitCost) })) });
      toast.success('Purchase recorded!'); onSave();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header"><h3>Record Purchase</h3><button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button></div>
        <div className="form-row">
          <div className="field"><label>Date</label><DateField value={date} onChange={e => setDate(e.target.value)} /></div>
          <div className="field"><label>Supplier (optional)</label>
            <select value={supplierId} onChange={e => setSupplierId(e.target.value)}>
              <option value="">Select supplier</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="field mb-4"><label>Note</label><input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Weekly restock" /></div>
        <div className="section-title">Items Purchased</div>
        <div className="item-row-header">
          {['Product','Qty','Unit Cost',''].map(h => <div key={h} style={{fontSize:11,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.6px'}}>{h}</div>)}
        </div>
        <div className="sale-items">
          {items.map((item, idx) => (
            <div className="sale-item-row" key={idx}>
              <select style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,color:'var(--text)',padding:'8px 10px',fontSize:13,outline:'none'}} value={item.productId} onChange={e => setItem(idx,'productId',e.target.value)}>
                <option value="">Select product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input type="number" min="0.01" step="0.01" style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,color:'var(--text)',padding:'8px',fontSize:13,outline:'none',textAlign:'right'}} value={item.quantity} onChange={e => setItem(idx,'quantity',e.target.value)} />
              <input type="number" min="0" step="0.01" style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,color:'var(--text)',padding:'8px',fontSize:13,outline:'none',textAlign:'right'}} value={item.unitCost} onChange={e => setItem(idx,'unitCost',e.target.value)} />
              <button className="btn btn-danger btn-sm" onClick={() => removeItem(idx)} style={{padding:'8px 10px'}}>✕</button>
            </div>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm mt-4" onClick={addItem}>+ Add Item</button>
        <div className="sale-total mt-4"><span>Total Cost</span><span style={{fontSize:20,color:'var(--warn)'}}>{fmt.currency(total)}</span></div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : '✓ Save Purchase'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Purchases() {
  const [result, setResult]       = useState({ items: [], totalCount: 0, page: 1, pageSize: 20, totalPages: 1 });
  const [products, setProducts]   = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded]   = useState(null);

  const [search, setSearch]         = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(20);

  const searchTimer = useRef();
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const loadPurchases = useCallback(() => {
    api.getPurchases({ page, pageSize, search: debouncedSearch || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, supplierId: supplierId || undefined })
      .then(setResult).catch(() => toast.error('Failed to load purchases'));
  }, [page, pageSize, debouncedSearch, dateFrom, dateTo, supplierId]);

  useEffect(() => { loadPurchases(); }, [loadPurchases]);
  useEffect(() => {
    Promise.all([api.getSuppliersLookup(), api.getProductsLookup()])
      .then(([s, p]) => { setSuppliers(s); setProducts(p); });
  }, []);

  const clearFilters = () => { setSearch(''); setDateFrom(''); setDateTo(''); setSupplierId(''); setPage(1); };
  const hasFilters = search || dateFrom || dateTo || supplierId;

  return (
    <div>
      <div className="flex-between mb-6">
        <div className="page-header" style={{margin:0}}>
          <h2>Purchases</h2>
          <p>{result.totalCount} total purchase orders</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Purchase</button>
      </div>

      <div className="card">
        <div className="filter-bar">
          <input placeholder="Search by product, supplier or note…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select value={supplierId} onChange={e => { setSupplierId(e.target.value); setPage(1); }}>
            <option value="">All suppliers</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <DateField value={dateFrom} title="From date"
            onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
          <DateField value={dateTo} title="To date"
            onChange={e => { setDateTo(e.target.value); setPage(1); }} />
          {hasFilters && <button className="filter-clear" onClick={clearFilters}>✕ Clear</button>}
          <div className="filter-spacer"/>
          <span className="filter-results">{result.totalCount} orders</span>
        </div>

        {result.items.length === 0 ? (
          <div className="empty-state"><div className="icon"><NavIcon name="purchases" size={32} /></div><div>No purchases found.</div></div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Supplier</th><th>Items</th><th>Total Cost</th><th>Note</th></tr></thead>
                <tbody>
                  {result.items.map(p => {
                    const total = p.items?.reduce((sum, i) => sum + i.quantity * i.unitCost, 0) || 0;
                    const isOpen = expanded === p.id;
                    return (
                      <>
                        <tr key={p.id} style={{cursor:'pointer'}} onClick={() => setExpanded(isOpen ? null : p.id)}>
                          <td className="mono" data-label="Date">{fmt.datetime(p.date)}</td>
                          <td data-label="Supplier">{p.supplier?.name || <span className="text-muted">—</span>}</td>
                          <td data-label="Items"><span className="badge badge-blue">{p.items?.length || 0} items</span></td>
                          <td className="mono text-warn" data-label="Total Cost" style={{fontWeight:700}}>{fmt.currency(total)}</td>
                          <td className="text-muted" data-label="Note">{p.note || '—'}</td>
                        </tr>
                        {isOpen && (
                          <tr key={`${p.id}-detail`}>
                            <td colSpan={5} style={{background:'var(--surface2)',padding:'10px 20px'}}>
                              <table style={{width:'100%'}}>
                                <thead><tr><th>Product</th><th>Qty</th><th>Unit Cost</th><th>Subtotal</th></tr></thead>
                                <tbody>
                                  {p.items?.map(i => (
                                    <tr key={i.id}>
                                      <td>{i.product?.name}</td>
                                      <td className="mono">{i.quantity}</td>
                                      <td className="mono">{fmt.currency(i.unitCost)}</td>
                                      <td className="mono text-warn">{fmt.currency(i.quantity * i.unitCost)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={result.page} totalPages={result.totalPages} pageSize={result.pageSize}
              totalCount={result.totalCount} onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
          </>
        )}
      </div>

      {showModal && <PurchaseModal products={products} suppliers={suppliers} onClose={() => setShowModal(false)} onSave={() => { setShowModal(false); loadPurchases(); }} />}
    </div>
  );
}
